import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { IMAGE_BASE_URL } from "../network/apiClient";
import BrandApi from "../apiProvider/brand";
import { toast } from "react-toastify";
import ReactTableComponent from "../table/ReactTableComponent";
import usePermission from "../hook/usePermission";
import Swal from "sweetalert2";

const BrandPage = () => {
  const canAdd = usePermission("brands", "add");
  const canEdit = usePermission("brands", "edit");
  const canDelete = usePermission("brands", "delete");

  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    brandName: "",
    Image: null, // New file selected
    existingImage: null, // Existing logo object { docPath, docName }
    displayOrder: 0,
  });

  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);

  // TABLE DATA STATES
  const [brandData, setBrandData] = useState([]);      // visible rows
  const [allBrandsCache, setAllBrandsCache] = useState(null);  // full dataset
  const [filteredBrands, setFilteredBrands] = useState(null);  // filtered dataset

  // PAGINATION STATES
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // SEARCH STATES
  const [search, setSearch] = useState("");
  const searchDebounceRef = useRef(null);
  const fetchIdRef = useRef(0);

  /* ---------------------------
      SERVER PAGE FETCH
  ----------------------------*/
  const fetchServerPage = async (page = 0) => {
    return await BrandApi.brandList({
      page,
      limit: pageSize,
      search: "",
    });
  };

  /* ---------------------------
      FETCH ALL PAGES (CACHE)
  ----------------------------*/
  const fetchAllPages = async (fetchIdAtCall) => {
    const first = await BrandApi.brandList({ page: 0, limit: pageSize, search: search });

    if (fetchIdAtCall !== fetchIdRef.current) return null;
    if (!first?.response) return null;

    let all = [...first.response.data];
    const totalPagesFromBackend = first.response.totalPages || 1;

    if (totalPagesFromBackend === 1) {
      setAllBrandsCache(all);
      return all;
    }

    const promises = [];
    for (let p = 1; p < totalPagesFromBackend; p++) {
      promises.push(BrandApi.brandList({ page: p, limit: pageSize, search: "" }));
    }

    const results = await Promise.all(promises);

    for (const r of results) {
      const d = r?.response;
      if (d?.data) all.push(...d.data);
    }

    setAllBrandsCache(all);
    return all;
  };

  /* ---------------------------
      ROOT-STYLE SEARCH ENGINE
  ----------------------------*/
  const fetchCurrentView = async () => {
    const fetchId = ++fetchIdRef.current;

    // 1) Search EMPTY → server pagination
    if (!search.trim()) {
      setFilteredBrands(null);
      setAllBrandsCache(null);

      const res = await fetchServerPage(pageIndex);
      if (fetchId !== fetchIdRef.current) return;

      const d = res?.response;
      setBrandData(d?.data || []);
      setTotalPages(d?.totalPages || 1);
      setTotal(d?.total || 0);
      return;
    }

    // 2) Search ACTIVE → use FULL dataset
    let all = allBrandsCache;

    if (!all) all = await fetchAllPages(fetchId);
    if (fetchId !== fetchIdRef.current) return;

    const q = search.trim().toLowerCase();

    const filtered = all.filter((item) => {
      const name = item.name?.toLowerCase() || "";
      return name.includes(q);
    });

    setFilteredBrands(filtered);
    const newTotalPages = Math.ceil(filtered.length / pageSize) || 1;
    setTotalPages(newTotalPages);

    const start = pageIndex * pageSize;
    setBrandData(filtered.slice(start, start + pageSize));
  };

  /* ---------------------------
      DEBOUNCED SEARCH INPUT
  ----------------------------*/
  const handleSearchChange = (e) => {
    const v = e.target.value;
    setSearch(v);
    setPageIndex(0);

    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    fetchIdRef.current += 1;
    fetchCurrentView();
  };

  /* ---------------------------
      INITIAL FETCH
  ----------------------------*/
  useEffect(() => {
    fetchCurrentView();
  }, [pageIndex, search]);

  /* ---------------------------
      FORM LOGIC
  ----------------------------*/
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      alert("Only JPG and PNG allowed.");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      Image: file,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    let newErrors = {};
    if (!formData.brandName.trim()) newErrors.brandName = "Brand Name is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ---------------------------
      SUBMIT (Create/Update)
  ----------------------------*/
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    // Always use FormData (backend uses multer, won't accept JSON)
    const formToSend = new FormData();
    formToSend.append("name", formData.brandName);
    formToSend.append("displayOrder", formData.displayOrder ?? 0);
    // Append the file if user picked a NEW one, otherwise append empty string
    // (multer ignores empty logo in edit mode and retains existing image — matches subcategory pattern)
    if (formData.Image instanceof File) {
      formToSend.append("logo", formData.Image);
    } else if (isEditMode && !formData.existingImage && !formData.Image) {
      // Explicitly send empty string to signal removal of existing logo
      formToSend.append("logo", "");
    }

    let response;
    if (isEditMode) {
      response = await BrandApi.updateBrand(formToSend, editId);
    } else {
      response = await BrandApi.brandcreate(formToSend);
    }

    if (response.status) {
      setAllBrandsCache(null);
      setFilteredBrands(null);
      fetchCurrentView();
      handleModalClose();

      const closeBtn = document.querySelector("#exampleModalEdit .btn-close");
      if (closeBtn) closeBtn.click();
    }
  };



  /* ---------------------------
      EDIT
  ----------------------------*/
  const handleEdit = async (id) => {
    const response = await BrandApi.brandDetails(id);
    if (response.status) {
      const data = response.response.data;

      // logo may be an object, an array, or null
      let logo = data.logo;
      if (Array.isArray(logo)) logo = logo[0] ?? null;

      setIsEditMode(true);
      setEditId(id);
      setFormData({
        brandName: data.name || "",
        Image: null, // Reset new image selection
        existingImage: logo ?? null, // Store existing logo info
        displayOrder: data.displayOrder ?? 0,
      });
    }
  };

  /* ---------------------------
      DELETE
  ----------------------------*/
  const handleRemoveRow = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const response = await BrandApi.deleteBrand(id);
        if (response.status) {
          setAllBrandsCache(null);
          setFilteredBrands(null);
          fetchCurrentView();
        }
      }
    });
  };

  /* ---------------------------
      COLUMNS
  ----------------------------*/
  const columns = useMemo(
    () => [
      {
        header: "S.No",
        size: 70,
        id: "sno",
        cell: (info) => pageIndex * pageSize + info.row.index + 1,
      },
      {
        header: "Name",
        accessorKey: "name",
      },
      {
        header: "Order",
        accessorKey: "displayOrder",
      },
      {
        header: "Logo",
        accessorKey: "logo",
        cell: (info) => {
          const logo = info.row.original.logo;
          if (logo && logo.docPath && logo.docName) {
            return (
              <img
                src={`${IMAGE_BASE_URL}/${logo.docPath}/${logo.docName}`}
                style={{ width: 40, height: 40, objectFit: "cover" }}
                className="rounded shadow-sm"
              />
            );
          }
          return (
            <div
              className="bg-light text-secondary rounded d-flex justify-content-center align-items-center"
              style={{ width: 40, height: 40, border: "1px dashed #cbd5e1" }}
            >
              <Icon icon="bi:image" style={{ fontSize: "20px" }} />
            </div>
          );
        },
      },
      {
        header: "Actions",
        cell: (info) => {
          const id = info.row.original._id;
          return (
            <div className="d-flex gap-2">
              {canEdit && (
                <button
                  type="button"
                  className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                  data-bs-toggle="modal"
                  data-bs-target="#exampleModalEdit"
                  onClick={() => handleEdit(id)}
                >
                  <Icon icon="lucide:edit" />
                </button>
              )}

              {canDelete && (
                <button
                  type="button"
                  className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                  onClick={() => handleRemoveRow(id)}
                >
                  <Icon icon="fluent:delete-24-regular" />
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [pageIndex]
  );

  /* ---------------------------
      PAGINATION BUTTONS
  ----------------------------*/
  const handleNextPage = () => {
    if (pageIndex + 1 < totalPages) setPageIndex((prev) => prev + 1);
  };

  const handlePreviousPage = () => {
    if (pageIndex > 0) setPageIndex((prev) => prev - 1);
  };

  const handleModalClose = () => {
    setFormData({ brandName: "", Image: null, existingImage: null, displayOrder: 0 });
    const fileInput = document.getElementById("brandImageInput");
    if (fileInput) fileInput.value = "";
    setErrors({});
  };

  return (
    <div className="card h-100 p-0 radius-12">
      {/* SEARCH + CREATE BUTTON */}
      <div className="card-header d-flex justify-content-between py-16 px-24">
        <div style={{ position: "relative" }}>
          <input
            type="text"
            className="form-control"
            placeholder="Search..."
            style={{ maxWidth: 350 }}
            value={search}
            onChange={handleSearchChange}
          />
          <Icon
            icon="ic:baseline-search"
            style={{
              position: "absolute",
              right: 10,
              top: 10,
              fontSize: 20,
            }}
          />
        </div>

        {canAdd && (
          <button
            className="btn btn-success catalog-add-btn"
            data-bs-toggle="modal"
            data-bs-target="#exampleModalEdit"
            onClick={() =>
              setFormData({ brandName: "", Image: null, existingImage: null }) || setIsEditMode(false)
            }
          >
            <Icon
              icon="ic:baseline-plus"
              className="icon text-xl line-height-1"
            />
            Create Brand
          </button>
        )}
      </div>

      {/* TABLE */}
      <div className="card-body p-24">
        <ReactTableComponent
          data={brandData}
          columns={columns}
          pageIndex={pageIndex}
          totalPages={totalPages}
          onNextPage={handleNextPage}
          onPreviousPage={handlePreviousPage}
          totalRecords={total}
        />
      </div>

      {/* MODAL (YOUR EXACT UI) */}
      {/* ---------------------------------------------------------- */}
      <div
        className="modal fade"
        id="exampleModalEdit"
        tabIndex={-1}
        aria-labelledby="exampleModalEditLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog modal-dialog-centered">
          <div className="modal-content radius-16 bg-base">
            <div className="modal-header py-16 px-24 border-bottom">
              <h1 className="modal-title fs-5" id="exampleModalEditLabel">
                {isEditMode ? "Edit Brand" : "Create Brand"}
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={handleModalClose}
              />
            </div>

            <div className="modal-body p-24">
              <form onSubmit={handleSubmit}>
                <div className="row gy-3">
                  <div className="col-12">
                    <label className="form-label">Brand Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      name="brandName"
                      className="form-control"
                      value={formData.brandName || ""}
                      onChange={handleChange}
                    />
                    {errors.brandName && (
                      <span className="text-danger">{errors.brandName}</span>
                    )}
                  </div>

                  <div className="col-12">
                    <label className="form-label">Order <span className="text-danger">*</span></label>
                    <input
                      type="number"
                      name="displayOrder"
                      className="form-control"
                      value={formData.displayOrder ?? 1}
                      min="1"
                      onChange={(e) => {
                        const value = parseInt(e.target.value, 10);
                        if (!isNaN(value) && value >= 1) {
                          setFormData((prev) => ({ ...prev, displayOrder: value }));
                        } else if (e.target.value === '') {
                          setFormData((prev) => ({ ...prev, displayOrder: '' }));
                        }
                        setErrors((prev) => ({ ...prev, displayOrder: '' }));
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-' || e.key === '.') {
                          e.preventDefault();
                        }
                      }}
                      required
                    />
                    {errors.displayOrder && (
                      <span className="text-danger">{errors.displayOrder}</span>
                    )}
                  </div>


                  <div className="col-12">
                    <label className="form-label">Brand Logo</label>
                    <input
                      id="brandImageInput"
                      className="form-control form-control-sm"
                      name="Image"
                      type="file"
                      accept="image/png, image/jpeg"
                      onChange={handleFileChange}
                      style={{ height: "52px" }}
                    />

                    <div className="mt-2" style={{ width: 80, height: 80 }}>
                      {formData.Image || (isEditMode && formData.existingImage?.docName) ? (
                        <div className="position-relative" style={{ width: 80, height: 80 }}>
                          <img
                            src={
                              formData.Image instanceof File
                                ? URL.createObjectURL(formData.Image)
                                : `${IMAGE_BASE_URL}/${formData.existingImage?.docPath}/${formData.existingImage?.docName}`
                            }
                            className="img-thumbnail"
                            style={{ width: 80, height: 80, objectFit: "cover" }}
                            alt="Brand logo preview"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "/default-image.png";
                            }}
                          />
                          <button
                            type="button"
                            className="position-absolute top-0 end-0 bg-danger text-white border-0 rounded-circle d-flex justify-content-center align-items-center shadow-sm"
                            style={{
                              width: "24px",
                              height: "24px",
                              transform: "translate(50%, -50%)",
                              zIndex: 10,
                              padding: 0,
                            }}
                            onClick={() => {
                              setFormData((prev) => ({ ...prev, Image: null, existingImage: null }));
                              const fileInput = document.getElementById("brandImageInput");
                              if (fileInput) fileInput.value = "";
                            }}
                          >
                            <Icon icon="material-symbols:close" style={{ fontSize: "16px" }} />
                          </button>
                        </div>
                      ) : (
                        <div
                          className="bg-light text-secondary rounded d-flex justify-content-center align-items-center"
                          style={{ width: 80, height: 80, border: "1px dashed #cbd5e1" }}
                        >
                          <Icon icon="bi:image" style={{ fontSize: "32px" }} />
                        </div>
                      )}
                    </div>

                    {errors.Image && (
                      <span className="text-danger">{errors.Image}</span>
                    )}
                  </div>

                  <div className="modal-footer">
                    {((isEditMode && canEdit) || (!isEditMode && canAdd)) && (
                      <button type="submit" className="btn btn-primary text-sm">
                        {isEditMode ? "Update" : "Submit"}
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandPage;
